import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  Map, 
  CloudSun, 
  ShieldCheck, 
  ExternalLink, 
  Plus, 
  Search,
  CheckCircle2,
  AlertCircle,
  Key,
  Cpu,
  Loader2,
  Lock,
  Zap,
  ChevronDown,
  Sparkles,
  RefreshCw,
  Trophy,
  Settings2
} from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import { cn } from '@/utils/cn';
import { useIntegrations } from './hooks/useIntegrations';
import { integrationsService } from './services/integrations.service';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { PersonasTab } from '../../ai-personas/components/PersonasTab';
import { supabase } from '@/core/database/supabase';
import { getRoleBadgeClasses } from '@/utils/role';

export function IntegrationsListPage() {
  const { integrations, querySearch, isLoading, setQuerySearch, refetch } = useIntegrations();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  
  const [activeTab, setActiveTab] = useState<'api' | 'personas' | 'quota'>('api');
  const [isConfiguring, setIsConfiguring] = useState<any>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  const [dailyLimit, setDailyLimit] = useState<number>(20);
  const [roleLimits, setRoleLimits] = useState<Record<string, number>>({});
  const [rolesList, setRolesList] = useState<{code: string, name: string}[]>([]);
  const [isSavingLimit, setIsSavingLimit] = useState(false);

  useEffect(() => {
    if (companyId) {
      supabase.from('companies').select('ai_daily_limit, role_ai_limits').eq('id', companyId).single().then(({ data }) => {
        if (data && data.ai_daily_limit !== null) setDailyLimit(data.ai_daily_limit);
        if (data && data.role_ai_limits) setRoleLimits(data.role_ai_limits);
      });
      supabase.from('roles').select('code, name').then(({ data }) => {
        if (data) setRolesList(data);
      });
    }
  }, [companyId]);

  const handleSaveLimit = async () => {
    setIsSavingLimit(true);
    try {
      await supabase.from('companies').update({ ai_daily_limit: dailyLimit, role_ai_limits: roleLimits }).eq('id', companyId);
      toast.success('Đã cập nhật hạn mức AI thành công!');
    } catch (e) {
      toast.error('Lỗi khi lưu hạn mức.');
    } finally {
      setIsSavingLimit(false);
    }
  };

  useEffect(() => {
    if (isConfiguring) {
      setApiKeyInput(isConfiguring.config?.api_key || '');
      setSelectedModel(isConfiguring.config?.config?.selectedModel || isConfiguring.models?.[0] || '');
      setTestResult(null);
    }
  }, [isConfiguring]);

  const handleSave = async () => {
    if (!apiKeyInput.trim() || !isConfiguring) return;
    setIsSaving(true);
    try {
      await integrationsService.saveConfig(companyId, isConfiguring.id, apiKeyInput, selectedModel);
      toast.success(`Đã cập nhật cấu hình cho ${isConfiguring.name}`);
      setIsConfiguring(null);
      setApiKeyInput('');
      refetch();
    } catch (error) {
      console.error(error);
      toast.error('Không thể lưu cấu hình. Vui lòng thử lại.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!apiKeyInput.trim()) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const isValid = await integrationsService.testConnection(isConfiguring.id, apiKeyInput);
      if (isValid) {
        setTestResult('success');
        toast.success('Kết nối thành công! Hệ thống đã sẵn sàng.', {
          icon: <Sparkles className="text-amber-500 w-5 h-5" />,
          className: "bg-emerald-50 border-emerald-200 text-emerald-900 font-medium"
        });
      } else {
        setTestResult('error');
        toast.error('API Key không hợp lệ.');
      }
    } catch (error) {
      setTestResult('error');
      toast.error('Lỗi kiểm tra kết nối.');
    } finally {
      setIsTesting(false);
    }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'bot': return <Bot className="w-5 h-5" />;
      case 'cpu': return <Cpu className="w-5 h-5" />;
      case 'map': return <Map className="w-5 h-5" />;
      case 'cloud-sun': return <CloudSun className="w-5 h-5" />;
      default: return <Key className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header section */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-sm font-medium text-slate-900">Tích hợp Hệ thống</h1>
          <p className="text-xs text-slate-500">Quản lý trung tâm dữ liệu và trí tuệ nhân tạo Landscape.</p>
        </div>
      </div>

      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-row items-center gap-2 flex-1">
          <div className="flex-1 min-w-0">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
              <input 
                type="text" 
                placeholder="Tìm kiếm dịch vụ..."
                className="pl-10 h-10 pr-4 py-2 bg-slate-50 border-none rounded-md text-xs focus:ring-1 focus:ring-emerald-500 outline-none w-full transition-all font-medium"
                value={querySearch}
                onChange={(e) => setQuerySearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 overflow-x-auto">
          <Button 
            variant="outline"
            className="h-10 shrink-0 border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all gap-2 text-xs font-medium px-4"
            onClick={() => refetch()}
          >
            <RefreshCw className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
            Làm mới
          </Button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('api')}
          className={cn(
            "pb-3 text-xs font-medium transition-colors relative flex items-center gap-2",
            activeTab === 'api' ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
          )}
        >
          <Key size={14} />
          API Keys (Nền tảng)
          {activeTab === 'api' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('personas')}
          className={cn(
            "pb-3 text-xs font-medium transition-colors relative flex items-center gap-2",
            activeTab === 'personas' ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
          )}
        >
          <Bot size={14} />
          Đội ngũ Đặc vụ AI
          {activeTab === 'personas' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('quota')}
          className={cn(
            "pb-3 text-xs font-medium transition-colors relative flex items-center gap-2",
            activeTab === 'quota' ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
          )}
        >
          <Settings2 size={14} />
          Hạn mức Hỏi AI
          {activeTab === 'quota' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-t-full" />
          )}
        </button>
      </div>

      {activeTab === 'api' && (
        <>
          {/* Grid List */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[240px] animate-pulse rounded-2xl bg-white border border-slate-100 shadow-sm" />
          ))
        ) : (
          integrations.map((item) => (
            <Card key={item.id} className="group overflow-hidden border-slate-200 bg-white hover:border-emerald-300 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 relative">
              <div className={cn("h-2 w-full bg-gradient-to-r transition-all duration-500 group-hover:h-3", item.color)} />
              <div className="p-6">
                <div className="flex justify-between items-start mb-5">
                  <div className={cn(
                    "p-3 rounded-2xl text-white shadow-xl transform transition-transform group-hover:scale-110",
                    "bg-gradient-to-br shadow-current/20", 
                    item.color
                  )}>
                    {getIcon(item.icon)}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {item.status === 'connected' ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 px-3 py-1 font-medium text-[10px] flex items-center gap-1.5 rounded-full shadow-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Live
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 border-slate-200 px-3 py-1 font-medium text-[10px] rounded-full">
                        Standby
                      </Badge>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-medium text-slate-900 group-hover:text-emerald-800 transition-colors">{item.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">{item.provider}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-200" />
                    <span className="text-[10px] text-slate-400 font-medium uppercase">{item.category}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-3 leading-relaxed line-clamp-2 min-h-[40px] font-medium">
                    {item.description}
                  </p>
                </div>

                <div className="mt-5 pt-5 border-t border-slate-50 flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-400 font-medium uppercase tracking-tighter">API Endpoint</span>
                    <span className="text-[11px] font-mono font-medium text-slate-500 mt-0.5">
                      {item.status === 'connected' ? 'SECURE_KEY_HASH' : 'Not configured'}
                    </span>
                  </div>
                  <Button 
                    variant={item.status === 'connected' ? 'outline' : 'primary'}
                    className={cn(
                      "h-9 px-5 text-xs font-medium transition-all rounded-xl shadow-lg",
                      item.status !== 'connected' && "bg-emerald-800 hover:bg-emerald-900 text-white border-none"
                    )}
                    onClick={() => setIsConfiguring(item)}
                  >
                    {item.status === 'connected' ? 'Hiệu chỉnh' : 'Cấu hình'}
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Config Modal - VIP Edition */}
      {isConfiguring && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
          <Card className={cn(
            "w-full max-w-lg bg-white shadow-[0_0_50px_rgba(0,0,0,0.3)] border-none overflow-hidden animate-in zoom-in-95 duration-300 relative rounded-3xl",
            testResult === 'success' && "ring-4 ring-emerald-500/30"
          )}>
            {/* VIP Success Background Effect */}
            {testResult === 'success' && (
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent pointer-events-none animate-in fade-in duration-700" />
            )}

            <div className={cn(
              "h-2.5 w-full bg-gradient-to-r transition-all duration-1000", 
              testResult === 'success' ? "from-emerald-400 to-teal-600" : isConfiguring.color
            )} />
            
            <div className="p-10 relative z-10">
              <div className="flex items-center gap-5 mb-10">
                <div className={cn(
                  "p-4 rounded-2xl text-white shadow-2xl transition-all duration-700", 
                  "bg-gradient-to-br transform",
                  testResult === 'success' ? "from-emerald-500 to-teal-700 scale-110 rotate-3" : isConfiguring.color
                )}>
                  {testResult === 'success' ? <Trophy className="w-6 h-6 animate-bounce" /> : getIcon(isConfiguring.icon)}
                </div>
                <div>
                  <h3 className="text-2xl font-medium text-slate-900 tracking-tight">
                    {testResult === 'success' ? 'Cấu hình Hoàn tất!' : `Cấu hình ${isConfiguring.name}`}
                  </h3>
                  <p className="text-sm text-slate-500 font-medium">
                    {testResult === 'success' ? 'Kết nối đã được xác thực cấp độ VIP.' : 'Thiết lập tham số và khóa bảo mật cấp cao.'}
                  </p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Model Selection */}
                {isConfiguring.models && isConfiguring.models.length > 0 && (
                  <div className="space-y-3">
                    <label className="text-[11px] font-medium text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <Zap size={14} className="text-amber-500 fill-amber-500" />
                      Lựa chọn mô hình (AI Model)
                    </label>
                    <div className="relative group">
                      <select 
                        className="w-full pl-4 pr-12 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-medium appearance-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all cursor-pointer group-hover:bg-white"
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                      >
                        {isConfiguring.models.map((model: any) => {
                          const mId = typeof model === 'string' ? model : model.id;
                          const mLabel = typeof model === 'string' ? model : model.label;
                          return <option key={mId} value={mId}>{mLabel}</option>;
                        })}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none group-hover:text-emerald-500 transition-colors" />
                    </div>
                    {isConfiguring.models.find((m: any) => (typeof m === 'string' ? m : m.id) === selectedModel)?.description && (
                      <p className="text-[11px] text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-100 flex items-start gap-2 italic leading-relaxed animate-in slide-in-from-top-1 duration-300">
                        <i className="ri-information-line text-emerald-500 text-sm mt-0.5" />
                        {isConfiguring.models.find((m: any) => (typeof m === 'string' ? m : m.id) === selectedModel).description}
                      </p>
                    )}
                  </div>
                )}

                {/* API Key Input */}
                <div className="space-y-3">
                  <label className="text-[11px] font-medium text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <Key size={14} className="text-slate-400" />
                    API Key / Access Token
                  </label>
                  <div className="relative group">
                    <input 
                      type="password"
                      placeholder="Nhập mã key bí mật của bạn..."
                      className={cn(
                        "w-full pl-4 pr-32 py-4 bg-slate-50 border-2 rounded-2xl text-sm focus:ring-4 outline-none transition-all font-mono font-medium group-hover:bg-white",
                        testResult === 'success' ? "border-emerald-500 bg-emerald-50/30 ring-emerald-500/10" : "border-slate-100 focus:ring-emerald-500/10 focus:border-emerald-500"
                      )}
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className={cn(
                          "h-10 px-4 text-xs font-medium rounded-xl transition-all shadow-sm active:scale-95",
                          testResult === 'success' ? "text-white bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200" : 
                          testResult === 'error' ? "text-rose-600 bg-rose-50" : "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                        )}
                        onClick={handleTest}
                        disabled={isTesting || !apiKeyInput.trim()}
                      >
                        {isTesting ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : testResult === 'success' ? (
                          <div className="flex items-center gap-2">
                            <Sparkles size={14} />
                            Verified
                          </div>
                        ) : testResult === 'error' ? (
                          <AlertCircle size={16} />
                        ) : 'Kiểm tra VIP'}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-1">
                    <p className="text-[10px] text-slate-400 font-medium italic flex items-center gap-1.5 uppercase">
                      <ShieldCheck size={12} className="text-emerald-500" /> AES-256 Multi-layer Protection
                    </p>
                    {testResult === 'success' && (
                      <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-md text-[10px] font-medium animate-in slide-in-from-right-4">
                        <CheckCircle2 size={10} />
                        READY TO USE
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-12">
                <Button 
                  variant="ghost" 
                  className="flex-1 h-14 text-slate-500 hover:bg-slate-50 font-medium text-sm rounded-2xl transition-all"
                  onClick={() => {
                    setIsConfiguring(null);
                    setApiKeyInput('');
                  }}
                  disabled={isSaving}
                >
                  Hủy bỏ
                </Button>
                <Button 
                  className={cn(
                    "flex-1 h-14 font-medium text-sm shadow-2xl active:scale-95 transition-all border-none rounded-2xl relative overflow-hidden",
                    testResult === 'success' 
                      ? "bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-emerald-500/40" 
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  )}
                  onClick={handleSave}
                  disabled={isSaving || !apiKeyInput.trim() || isTesting}
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <div className="flex items-center gap-2">
                      {testResult === 'success' && <Sparkles size={18} className="animate-pulse" />}
                      Lưu cấu hình VIP
                    </div>
                  )}
                  {testResult === 'success' && (
                    <div className="absolute inset-0 bg-white/10 animate-pulse pointer-events-none" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      </>
      )}

      {activeTab === 'quota' && (
        <Card className="p-6 bg-white border-2 border-slate-100 shadow-sm relative overflow-hidden rounded-3xl mt-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl border border-emerald-100 shrink-0">
                  <Settings2 size={24} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-medium text-base tracking-tight">Hạn mức Hỏi AI (Daily Quota)</h4>
                  <p className="text-slate-500 text-xs mt-1 font-medium">
                    Giới hạn số lượng câu hỏi mỗi người dùng được phép sử dụng trong ngày để tối ưu chi phí. Hạn mức vai trò (nếu có) sẽ ghi đè lên hạn mức mặc định.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="flex items-center gap-3 bg-slate-50 p-2 border border-slate-100 rounded-2xl">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 uppercase ml-2">Mặc định:</span>
                    <input 
                      type="number" 
                      className="w-20 px-3 py-2 text-center font-medium text-slate-800 bg-white border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 rounded-xl outline-none transition-all"
                      value={dailyLimit}
                      onChange={(e) => setDailyLimit(parseInt(e.target.value) || 0)}
                      min="0"
                      max="1000"
                    />
                    <span className="text-xs font-medium text-slate-400 uppercase mr-2">Lượt/Ngày</span>
                  </div>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-10 px-6 rounded-xl shadow-md transition-all active:scale-95 border-none"
                    onClick={handleSaveLimit}
                    disabled={isSavingLimit}
                  >
                    {isSavingLimit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lưu cài đặt'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Roles Limit Config */}
            {rolesList.length > 0 && (
              <div className="pt-5 border-t border-slate-100">
                <h5 className="text-sm font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-500" />
                  Cấu hình riêng biệt theo Vai trò
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {rolesList.map(role => (
                    <div key={role.code} className="flex items-center justify-between bg-slate-50 hover:bg-white border border-slate-100 hover:border-emerald-200 p-2 rounded-xl transition-colors">
                      <span className={cn(getRoleBadgeClasses(role.code), "truncate mr-2")} title={role.name}>{role.name}</span>
                      <input 
                        type="number"
                        className="w-16 px-2 py-1 text-center font-medium text-slate-800 bg-white border border-slate-200 focus:border-emerald-500 rounded-lg outline-none text-xs"
                        value={roleLimits[role.code] !== undefined ? roleLimits[role.code] : ''}
                        placeholder={dailyLimit.toString()}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRoleLimits(prev => {
                            const next = { ...prev };
                            if (val === '') delete next[role.code];
                            else next[role.code] = parseInt(val) || 0;
                            return next;
                          });
                        }}
                        min="0"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'personas' && (
        <PersonasTab />
      )}
    </div>
  );
}
